import { useState, useEffect } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from "react-router";
import { AppShell, Burger, NavLink, Title, Group, Flex, Button, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useAppDispatch, useAppSelector, appRoutes } from '@/shared';
import { selectWithdrawalsCreated } from '@/modules/withdrawals';
import { selectPaymentsApprovedDelay, selectPaymentsApprovedWrongAmount } from '@/modules/payments';
import { useGetH2HPaymentCountsQuery } from '@/modules/h2h-payments';
import { exitUser } from '@/modules/auth';

import {
  // Home,
  Payments,
  H2HPayments,
  Withdrawals,
  // WithdrawalMethods,
  // Config,
  // Users,
  Shops,
  // ShopCommissions,
  Transactions,
  // PaymentSystems,
  PaymentMethods,
  ConversionPairs,
  // Whitelist,
} from '@/pages';

import {
  // IconHome2,
  // IconUsers,
  // IconSettings,
  IconCreditCardRefund,
  IconCreditCardPay,
  IconShoppingBag,
  // IconPercentage,
  IconTransitionTop,
  // IconPremiumRights,
  IconDeviceIpadHorizontal,
  IconArrowsExchange,
  IconCreditCardFilled,
  // IconChecklist,
  // IconArrowAutofitRight,
} from '@tabler/icons-react';

import type { TDashboardMenuItem, TRoute } from './store/types';

const menuItems: TDashboardMenuItem[] = [
  // {
  //   id: appRoutes.dashboard,
  //   title: 'Главная',
  //   path: '',
  //   icon: <IconHome2 size={16} stroke={1.5} />,
  // },
  {
    id: appRoutes.payments,
    title: 'Платежи',
    path: appRoutes.payments,
    icon: <IconCreditCardRefund size={16} stroke={1.5} />
  },
  {
    id: appRoutes.h2hPayments,
    title: 'Платежи H2H',
    path: appRoutes.h2hPayments,
    icon: <IconCreditCardFilled size={16} stroke={1.5} />
  },
  {
    id: appRoutes.withdrawals,
    title: 'Заявки на вывод',
    path: appRoutes.withdrawals,
    icon: <IconCreditCardPay size={16} stroke={1.5} />
  },
  // {
  //   id: appRoutes.withdrawalMethods,
  //   title: 'Методы вывода',
  //   path: appRoutes.withdrawalMethods,
  //   icon: <IconArrowAutofitRight size={16} stroke={1.5} />,
  // },
  // {
  //   id: appRoutes.config,
  //   title: 'Настройки',
  //   path: appRoutes.config,
  //   icon: <IconSettings size={16} stroke={1.5} />,
  // },
  // {
  //   id: appRoutes.users,
  //   title: 'Пользователи',
  //   path: appRoutes.users,
  //   icon: <IconUsers size={16} stroke={1.5} />,
  // },
  {
    id: appRoutes.shops,
    title: 'Магазины',
    path: appRoutes.shops,
    icon: <IconShoppingBag size={16} stroke={1.5} />,
  },
  // {
  //   id: appRoutes.shopCommissions,
  //   title: 'Комиссии магазинов',
  //   path: appRoutes.shopCommissions,
  //   icon: <IconPercentage size={16} stroke={1.5} />,
  // },
  {
    id: appRoutes.transactions,
    title: 'Транзакции',
    path: appRoutes.transactions,
    icon: <IconTransitionTop size={16} stroke={1.5} />,
  },
  // {
  //   id: appRoutes.paymentSystems,
  //   title: 'Платёжные системы',
  //   path: appRoutes.paymentSystems,
  //   icon: <IconPremiumRights size={16} stroke={1.5} />,
  // },
  {
    id: appRoutes.paymentMethods,
    title: 'Платёжные методы',
    path: appRoutes.paymentMethods,
    icon: <IconDeviceIpadHorizontal size={16} stroke={1.5} />,
  },
  {
    id: appRoutes.conversionPairs,
    title: 'Торговые пары',
    path: appRoutes.conversionPairs,
    icon: <IconArrowsExchange size={16} stroke={1.5} />,
  },
  // {
  //   id: appRoutes.whitelist,
  //   title: 'Белый список',
  //   path: appRoutes.whitelist,
  //   icon: <IconChecklist size={16} stroke={1.5} />,
  // },
];

const getNavlinkLabel = (
  item: TDashboardMenuItem,
  createdWithdrawals: number,
  approveRemotePayments: number,
  h2hActions: number,
) => (
  <Flex align={'flex-end'} gap={'sm'}>
    <Text span inherit>{item.title}</Text>
    {(item.id === 'withdrawals') && (createdWithdrawals > 0) && (
      <Text c={'red'} span inherit fw={'bold'} lh={'sm'}>({createdWithdrawals})</Text>
    )}
    {(item.id === 'payments') && (approveRemotePayments > 0) && (
      <Text c={'red'} span inherit fw={'bold'} lh={'sm'}>({approveRemotePayments})</Text>
    )}
    {(item.id === appRoutes.h2hPayments) && (h2hActions > 0) && (
      <Text c={'red'} span inherit fw={'bold'} lh={'sm'}>({h2hActions})</Text>
    )}
  </Flex>
);

export const OperatorDashboard = () => {
  const { pathname } = useLocation();
  const [active, setActive] = useState<TRoute>(appRoutes.dashboard);

  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);

  const dispatch = useAppDispatch();
  const createdWithdrawals = useAppSelector(selectWithdrawalsCreated);
  const approvedPaymentsDelay = useAppSelector(selectPaymentsApprovedDelay);
  const approvedPaymentsWrongAmount = useAppSelector(selectPaymentsApprovedWrongAmount);
  const approveRemotePayments = approvedPaymentsDelay + approvedPaymentsWrongAmount;
  const { data: h2hCounts } = useGetH2HPaymentCountsQuery({}, { pollingInterval: 30000 });
  const h2hActions = h2hCounts?.actionRequired ?? 0;

  // Устанавливает начальное значение активного роута
  useEffect(() => {
    if (pathname) {
      // Более точное сравнение путей
      const foundItem = menuItems.find((item) =>
        pathname === `/${appRoutes.dashboard}/${item.path}` ||
        pathname.startsWith(`/${appRoutes.dashboard}/${item.path}/`)
      );

      const initRoute = foundItem?.path ?? '';
      setActive(initRoute);
    }
  }, [pathname, menuItems]);

  const handleNavLinkClick = (path: TRoute) => {
    setActive(path);
    toggleMobile();
  };

  const handleExit = () => {
    dispatch(exitUser());
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 230,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      padding="xs"
    >

      <AppShell.Header style={{ maxWidth: '100%', overflow: 'hidden' }}>
        <Group h="100%" pr="20">
          <Flex justify="space-between" align="center" w="100%">
            <Group h="100%" px="md">
              <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
              <Burger opened={desktopOpened} onClick={toggleDesktop} visibleFrom="sm" size="sm" />
              <Title order={4} style={{ display: 'inline-block' }}>Boompay</Title>
            </Group>

            <Button
              variant="default"
              onClick={handleExit}
            >
              Выйти
            </Button>
          </Flex>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        {menuItems.map((item) => (
          <NavLink
            key={item.id}
            component={Link}
            // to={item.path}
            to={`/${appRoutes.dashboard}/${item.path}`}
            label={getNavlinkLabel(item, createdWithdrawals, approveRemotePayments, h2hActions)}
            leftSection={item.icon}
            active={item.path === active}
            onClick={() => handleNavLinkClick(item.path)}
          />
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          {/* <Route index element={<Home />} /> */}
          <Route path={appRoutes.payments} element={<Payments />} />
          <Route path={appRoutes.h2hPayments} element={<H2HPayments />} />
          <Route path={appRoutes.withdrawals} element={<Withdrawals />} />
          {/* <Route path={appRoutes.withdrawalMethods} element={<WithdrawalMethods />} /> */}
          {/* <Route path={appRoutes.config} element={<Config />} /> */}
          {/* <Route path={appRoutes.users} element={<Users />} /> */}
          <Route path={appRoutes.shops} element={<Shops />} />
          {/* <Route path={appRoutes.shopCommissions} element={<ShopCommissions />} /> */}
          <Route path={appRoutes.transactions} element={<Transactions />} />
          {/* <Route path={appRoutes.paymentSystems} element={<PaymentSystems />} /> */}
          <Route path={appRoutes.paymentMethods} element={<PaymentMethods />} />
          <Route path={appRoutes.conversionPairs} element={<ConversionPairs />} />
          {/* <Route path={appRoutes.whitelist} element={<Whitelist />} /> */}
          <Route path="*" element={<Navigate to={`/${appRoutes.dashboard}/${appRoutes.payments}`} />} />
        </Routes>
      </AppShell.Main>

      <AppShell.Footer zIndex={101}>
        <Group justify='center'>
          Boompay © 2026
        </Group>
      </AppShell.Footer>
    </AppShell>
  );
};
